import {
  DisplayMode,
  MarketingDetailCategoryInterface,
  MarketingDetailGroupInterface,
  MarketingDetailItemInterface,
  MarketingDetailsApiResponseInterface,
  SavingsSourceEnum,
  SectionType,
  MarketingDetailColumnItemInterface,
  MarketingDetailColumnGroupInterface
} from '../../models/marketing-detail.model';
import {FOOTER_GROUP_NAME} from '../../constants/marketing-detail.constants';

const SAVINGS_SOURCE_ITEM_NAME: string = 'SavingsSource';

function isNewShape(data: unknown): boolean {
  return typeof data === 'object' && data !== null && !Array.isArray(data);
}

function transformBenefitDetailsItem(legacy: MarketingDetailColumnItemInterface): MarketingDetailItemInterface {
  return {
    description: legacy.column1 || '',
    quantity: legacy.column2 || null,
    youPay: null,
    youSave: legacy.column3 || null,
    hasException: false,
    exceptionText: null,
    asteriskNotation: null,
    memberPays: null,
    adaCode: null,
    sectionType: SectionType.BENEFIT_DETAILS
  };
}

function transformCDPSItem(legacy: MarketingDetailColumnItemInterface): MarketingDetailItemInterface {
  return {
    description: legacy.column2 || '',
    quantity: null,
    youPay: null,
    youSave: null,
    hasException: false,
    exceptionText: null,
    asteriskNotation: null,
    memberPays: legacy.column3 || null,
    adaCode: legacy.column1 || null,
    sectionType: SectionType.COMMON_DENTAL_PLAN_SAVINGS
  };
}

function transformFooterItem(legacy: MarketingDetailColumnItemInterface, sectionType: SectionType): MarketingDetailItemInterface {
  return {
    description: legacy.column1 || '',
    quantity: null,
    youPay: null,
    youSave: null,
    hasException: false,
    exceptionText: null,
    asteriskNotation: null,
    memberPays: null,
    adaCode: null,
    sectionType: sectionType
  };
}

function extractSavingsSource(legacyGroups: MarketingDetailColumnGroupInterface[]): SavingsSourceEnum | undefined {
  const marker: MarketingDetailColumnGroupInterface | undefined = legacyGroups.find(group => group.itemName === SAVINGS_SOURCE_ITEM_NAME);
  const rawValue: string | undefined = marker?.value?.[0]?.column1;
  return rawValue === SavingsSourceEnum.FEE_SCHEDULE ? SavingsSourceEnum.FEE_SCHEDULE : undefined;
}

function transformLegacyGroups(
  legacyGroups: MarketingDetailColumnGroupInterface[],
  sectionType: SectionType,
  transformItem: (legacy: MarketingDetailColumnItemInterface) => MarketingDetailItemInterface,
  extractSettingRows: boolean = false
): MarketingDetailCategoryInterface {
  const source: MarketingDetailColumnGroupInterface[] = legacyGroups || [];
  const contentGroups: MarketingDetailColumnGroupInterface[] = extractSettingRows
    ? source.filter(group => group.itemName !== SAVINGS_SOURCE_ITEM_NAME)
    : source;

  const groups: MarketingDetailGroupInterface[] = contentGroups.map(legacyGroup => ({
    itemName: legacyGroup.itemName,
    items: (legacyGroup.value || []).map(item =>
      legacyGroup.itemName === FOOTER_GROUP_NAME ? transformFooterItem(item, sectionType) : transformItem(item)
    )
  }));

  return {
    displayMode: DisplayMode.SAVINGS_ONLY,
    exceptions: [],
    groups: groups,
    xrayExamFooterText: null,
    savingsSource: extractSettingRows ? extractSavingsSource(source) : undefined
  };
}

export function transformMarketingDetails(response: MarketingDetailsApiResponseInterface): MarketingDetailsApiResponseInterface {
  if (!response?.salesPlanMarketingDetails) return response;

  const marketingDetails = response.salesPlanMarketingDetails;

  if (marketingDetails.planBenefits && !isNewShape(marketingDetails.planBenefits)) {
    marketingDetails.planBenefits = transformLegacyGroups(
      marketingDetails.planBenefits as unknown as MarketingDetailColumnGroupInterface[],
      SectionType.BENEFIT_DETAILS,
      transformBenefitDetailsItem
    );
  }

  if (marketingDetails.commonDentalPlanSavings && !isNewShape(marketingDetails.commonDentalPlanSavings)) {
    marketingDetails.commonDentalPlanSavings = transformLegacyGroups(
      marketingDetails.commonDentalPlanSavings as unknown as MarketingDetailColumnGroupInterface[],
      SectionType.COMMON_DENTAL_PLAN_SAVINGS,
      transformCDPSItem,
      true
    );
  }

  return response;
}
